import { Create, Edit, useDataProvider, useNotify, useRedirect, useResourceContext } from 'react-admin';
import { BannerTargetingsForm } from './forms/banner-targetings';
import { useSearchParams } from 'react-router-dom';
import { useTransformWithCampaignId } from './forms/hooks/useTransformWithCampaignId';
import {
  resolveCreativeParentLineItemShowPath,
  withPlacementsLineItemIdFromNativeForEdit,
} from 'src/client/utility/pio-native-lineitem-fk-reads';

interface BannerTargetingsCreateInitialValues {
  placementsLineItemId: string;
}

export const BannerTargetingsCreate = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const [searchParams] = useSearchParams();
  const placementsLineItemId = searchParams.get('placementsLineItemId') ?? '';
  const transform = useTransformWithCampaignId();

  const resource = useResourceContext();

  const record: BannerTargetingsCreateInitialValues = {
    placementsLineItemId,
  };

  // The legacy /line_items show page can no longer load (PIO API shut off), so
  // resolve a legacy numeric id to its native UUID and land on /lineItems.
  // Prefer the submitted selection over the seeding query param — the user may
  // have picked a different line item in the form.
  const onSuccess = async (data?: Record<string, any>, variables?: Record<string, any>) => {
    notify('Saved new Banner Targeting');
    const path = await resolveCreativeParentLineItemShowPath(dataProvider, variables?.data, { placementsLineItemId });
    redirect(path ?? 'list', resource);
  };

  return (
    <Create
      record={record}
      mutationOptions={{
        onSuccess,
      }}
      redirect="list"
      transform={transform}
    >
      <BannerTargetingsForm type="Create" />
    </Create>
  );
};

export const BannerTargetingsEdit = () => {
  const notify = useNotify();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();
  const resource = useResourceContext();

  const onSuccess = async (data?: Record<string, any>, variables?: Record<string, any>) => {
    notify('Saved Banner Targeting');
    // MD-5145: resolve the parent line item from the edited record (submitted data /
    // previousData), not the mutation response. All rows route to the native
    // /lineItems show page; a legacy-only id is translated via externalPioId.
    const path = await resolveCreativeParentLineItemShowPath(dataProvider, variables?.data, variables?.previousData, data);
    redirect(path ?? 'list', resource);
  };

  return (
    <Edit
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess,
      }}
      redirect="list"
      queryOptions={{ select: withPlacementsLineItemIdFromNativeForEdit }}
    >
      <BannerTargetingsForm type="Update" />
    </Edit>
  );
};
